import api from './api';

export interface RagChatRequest {
  message: string;
  conversationId?: string;
  mode?: 'chat' | 'query';
}

export interface RagSource {
  documentName: string;
  documentId?: string;
  excerpt?: string;
  score?: number;
}

export interface RagChatResponse {
  response: string;
  conversationId?: string;
  sources: RagSource[];
  responseTimeMs: number;
  success: boolean;
  error?: string;
}

export interface RagDocument {
  dokumentID: number;
  dokumentName: string;
  originalDateiname: string;
  dateiTyp: string;
  dateiGroesse: number;
  status: string;
  hochgeladenAm: string;
  hochgeladenVon: string;
}

export interface RagWorkspace {
  workspaceSlug: string;
  workspaceName: string;
  documentCount: number;
  createdAt?: string;
  isActive: boolean;
}

export interface RagStats {
  totalChats: number;
  totalDocuments: number;
  totalStorageBytes: number;
  storageFormatted: string;
  chatsLast24h: number;
  chatsLast7d: number;
  chatsLast30d: number;
  avgResponseTimeMs: number;
}

export interface RagChatHistorie {
  chatID: number;
  anfrage: string;
  antwort?: string;
  dauerMs?: number;
  erfolg: boolean;
  erstelltAm: string;
  benutzer?: string;
}

/**
 * RAG (Retrieval-Augmented Generation) Service
 * Kommuniziert mit AnythingLLM über das Skeena Backend
 */
class RagService {
  /**
   * Sendet eine Chat-Nachricht an die mandanten-spezifische Wissensdatenbank
   */
  async chat(request: RagChatRequest): Promise<RagChatResponse> {
    const response = await api.post<RagChatResponse>('/rag/chat', request);
    return response.data;
  }

  /**
   * Lädt ein Dokument in die Wissensdatenbank hoch
   */
  async uploadDocument(file: File, description?: string): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    if (description) {
      formData.append('description', description);
    }

    const response = await api.post('/rag/documents/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  /**
   * Holt alle Dokumente in der Wissensdatenbank
   */
  async getDocuments(): Promise<RagDocument[]> {
    const response = await api.get<RagDocument[]>('/rag/documents');
    return response.data;
  }

  /**
   * Löscht ein Dokument aus der Wissensdatenbank
   */
  async deleteDocument(documentId: number): Promise<void> {
    await api.delete(`/rag/documents/${documentId}`);
  }

  /**
   * Holt Workspace-Informationen
   */
  async getWorkspace(): Promise<RagWorkspace> {
    const response = await api.get<RagWorkspace>('/rag/workspace');
    return response.data;
  }

  /**
   * Holt RAG-Statistiken
   */
  async getStats(): Promise<RagStats> {
    const response = await api.get<RagStats>('/rag/stats');
    return response.data;
  }

  /**
   * Holt Chat-Historie
   */
  async getHistory(limit: number = 50): Promise<RagChatHistorie[]> {
    const response = await api.get<RagChatHistorie[]>(`/rag/history?limit=${limit}`);
    return response.data;
  }

  /**
   * Testet die Verbindung zu AnythingLLM (nur für Admins)
   */
  async testConnection(): Promise<{ connected: boolean; message: string }> {
    const response = await api.get('/rag/test-connection');
    return response.data;
  }
}

export const ragService = new RagService();
export default ragService;
